import { useEffect, useRef, useState } from "react";
import { getMint } from "@solana/spl-token";
import { PublicKey } from "@solana/web3.js";
import { useConnection } from "@solana/wallet-adapter-react";

/**
 * Resolve and cache SPL mint decimals on-chain.
 *
 * kySOL uses 9 decimals while reward tokens vary (many are 6), so we never
 * hardcode a divisor — each mint's decimals are read from the chain once and
 * cached for the lifetime of the page. Mints that fail to resolve are left
 * undefined so callers can fall back explicitly.
 */
export const useMintDecimals = (mints: string[]) => {
  const { connection } = useConnection();
  const cacheRef = useRef<Record<string, number>>({});
  const [decimalsByMint, setDecimalsByMint] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);

  // Stable dependency: sorted, de-duped list of requested mints.
  const mintsKey = [...new Set(mints.filter(Boolean))].sort().join(",");

  useEffect(() => {
    const uniqueMints = mintsKey ? mintsKey.split(",") : [];
    const missing = uniqueMints.filter((mint) => cacheRef.current[mint] === undefined);

    if (missing.length === 0) {
      setDecimalsByMint((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const mint of uniqueMints) {
          if (next[mint] !== cacheRef.current[mint]) {
            next[mint] = cacheRef.current[mint];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      await Promise.all(
        missing.map(async (mint) => {
          try {
            const info = await getMint(connection, new PublicKey(mint));
            cacheRef.current[mint] = info.decimals;
          } catch (err) {
            console.error(`Error fetching decimals for mint ${mint}:`, err);
          }
        })
      );
      if (cancelled) return;
      setDecimalsByMint({ ...cacheRef.current });
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mintsKey, connection]);

  return { decimalsByMint, isLoading };
};
